import { NO_DOCUMENTS_MESSAGE } from '@/lib/api/base-formation';
import type { DocumentListItem, DocumentParty } from '@/lib/api/documents';
import { formatDateTime } from '@/lib/utils/format';

/**
 * One definition of "indexed" for the Documents screen and the chat header.
 *
 * A document is indexed when its row says so: `process_status` is `indexed`
 * (the latest parsed read of its current version has searchable passages),
 * which is what the row's Indexed / Classified chip shows (`doc-card.tsx`).
 * Counts come from the same documents list the rows render (every
 * non-deleted document), so "M indexed" follows the list's polling and can
 * never exceed "N docs".
 *
 * The header used to take M from the search-index health probe's
 * `document_count`: every document row ever created, deleted and failed ones
 * included, refreshed on its own slower clock. Payroll read "15 docs · 15
 * indexed" under "no document has been indexed"; Oregon Metro read "8 docs ·
 * 9 indexed" with three files still indexing.
 */

type IndexFacts = Pick<DocumentListItem, 'process_status'>;
type BaselineFacts = Pick<DocumentListItem, 'process_status' | 'source' | 'purpose'>;

/** The document's row reads Indexed (or Classified). */
export function isIndexedDocument(doc: IndexFacts): boolean {
  return doc.process_status === 'indexed';
}

export function countIndexedDocuments(documents: readonly IndexFacts[]): number {
  return documents.filter(isIndexedDocument).length;
}

/**
 * The Documents header line: "{n} docs · {m} indexed · last sync {time}".
 * Both counts come from the documents list, so nothing is said about them
 * until it has loaded; "last sync" is the engagement-wide last indexing time
 * from the search-index health probe, left out until it is known.
 */
export function documentsSubtitle(
  documents: readonly IndexFacts[] | undefined,
  lastIndexedAt: string | null | undefined,
): string {
  const bits: string[] = [];
  if (documents) {
    const n = documents.length;
    bits.push(`${n} doc${n === 1 ? '' : 's'}`);
    bits.push(`${countIndexedDocuments(documents)} indexed`);
  }
  if (lastIndexedAt) bits.push(`last sync ${formatDateTime(lastIndexedAt)}`);
  return bits.join(' · ');
}

/**
 * The parties the contract baseline reads (E19 rule 3, `BASELINE_PARTIES` in
 * `@caliber-2/ai-base-formation`): the vendor's and Avero's documents. Client
 * documents are never part of it.
 */
const BASELINE_PARTIES: ReadonlySet<DocumentParty> = new Set<DocumentParty>(['vendor', 'avero']);

/**
 * An indexed document the contract baseline can read: a vendor or Avero
 * document that is not an interview transcript (only the discovery
 * transcript reader reads those, T-0176). The run also leaves out documents
 * classified as ongoing material (status reports and the like); the list
 * does not carry that routing, so the wording below allows for it.
 */
export function isBaselineReadable(doc: BaselineFacts): boolean {
  return (
    isIndexedDocument(doc) &&
    BASELINE_PARTIES.has(doc.source) &&
    doc.purpose !== 'discovery_transcript'
  );
}

/** Indexed documents exist, but none is a vendor or Avero document the baseline reads. */
export const NO_BASELINE_DOCUMENTS_MESSAGE =
  "The contract baseline reads only vendor and Avero documents, and none of the indexed documents is one: they are client documents or interview transcripts. If one of them is the vendor's or Avero's, open it, change whose document it is, and run extraction again.";

/** Vendor or Avero documents are indexed now, but the last run found none to read. */
export const NO_BASELINE_DOCUMENTS_LAST_RUN_MESSAGE =
  'The last extraction found no vendor or Avero document to read. Run extraction again to read the ones indexed or re-tagged since; status reports and other ongoing documents are not part of the contract baseline.';

export interface NothingToReadNote {
  title: string;
  message: string;
}

/**
 * What the banner says when the latest contract-baseline run ended with
 * `ai.no_documents`, judged against the documents the screen lists:
 *
 *   - no listed document is indexed: "Nothing to read yet" (the run's own
 *     reason, which is then true);
 *   - documents are indexed but none is a vendor or Avero document the
 *     baseline reads: that the baseline reads only those, and none of the
 *     indexed documents is one;
 *   - a vendor or Avero document is indexed: the last run found none to
 *     read, so run again (the run may predate the document, or its party
 *     change).
 */
export function nothingToReadNote(documents: readonly BaselineFacts[]): NothingToReadNote {
  if (countIndexedDocuments(documents) === 0) {
    return { title: 'Nothing to read yet', message: NO_DOCUMENTS_MESSAGE };
  }
  if (!documents.some(isBaselineReadable)) {
    return {
      title: 'No vendor or Avero document to read',
      message: NO_BASELINE_DOCUMENTS_MESSAGE,
    };
  }
  return {
    title: 'The last extraction had nothing to read',
    message: NO_BASELINE_DOCUMENTS_LAST_RUN_MESSAGE,
  };
}
